import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Clock, RefreshCw, Loader2, Printer, Activity, Layers, Coffee,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { printReport } from "@/lib/printReport";
import { usePrintTemplate } from "@/hooks/usePrintTemplate";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  return d.message_en || d.message_ar || JSON.stringify(d);
};

const fmtHours = (mins, isAr) => {
  if (mins == null) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return isAr ? `${h}س ${m}د` : `${h}h ${m}m`;
  if (h > 0) return isAr ? `${h} س` : `${h}h`;
  return isAr ? `${m} د` : `${m}m`;
};

export default function ActiveServiceHoursReport({ range, branch }) {
  const { t, lang } = useLanguage();
  const template = usePrintTemplate();
  const isAr = lang === "ar";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (range?.start) q.set("start", range.start);
      if (range?.end) q.set("end", range.end);
      if (branch) q.set("branch", branch);
      const r = await api.get(`/reports/active-service-hours?${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.start, range?.end, branch]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const meta = data?.meta || {};

  const dutyNote = () => {
    if (meta.employee_rota_configured || meta.duty_basis === "rota") {
      return isAr
        ? t("active_service_hours_rota_note")
        : t("active_service_hours_rota_note");
    }
    if (meta.branch_hours_configured) {
      return isAr
        ? "وقت الفراغ = ساعات عمل الفرع في أيام الجلسات فقط (لا يوجد جدول دوام فردي)."
        : "Idle = branch open hours on session days only (no per-staff rota configured).";
    }
    return isAr
      ? "لم تُضبط ساعات عمل الفرع — يُستخدم افتراض 09:00–22:00 لأيام الجلسات فقط."
      : "Branch hours not configured — using 09:00–22:00 default on session days only.";
  };

  const dutyNoteTone = () => (
    meta.employee_rota_configured || meta.duty_basis === "rota"
      ? "text-emerald-800 bg-emerald-50 border-emerald-100"
      : "text-amber-700 bg-amber-50 border-amber-100"
  );

  const print = () => {
    if (!data) return;
    printReport({
      title: t("active_service_hours"),
      subtitle: isAr
        ? "ساعات الخدمة النشطة — الاستغلال مقابل عبء العمل"
        : "Active service hours — utilization vs workload",
      lang,
      template,
      meta: [
        { label: isAr ? "الفترة" : "Period", value: `${range?.start || "—"} → ${range?.end || "—"}` },
      ],
      columns: [
        isAr ? "الموظف" : "Employee",
        t("duty_days"),
        t("shift_duty_avg"),
        t("utilization_hours"),
        t("workload_hours"),
        t("idle_time_avg"),
        t("utilization_pct"),
      ],
      rows: rows.map((r) => [
        r.employee_name,
        { value: r.duty_days ?? "—", className: "num" },
        { value: fmtHours(r.shift_duty_avg_minutes, isAr), className: "num" },
        { value: fmtHours(r.utilization_minutes, isAr), className: "num" },
        { value: fmtHours(r.workload_minutes, isAr), className: "num" },
        { value: fmtHours(r.idle_avg_minutes, isAr), className: "num" },
        { value: r.utilization_pct != null ? `${r.utilization_pct}%` : "—", className: "num" },
      ]),
      totals: [
        { label: t("utilization_hours"), value: fmtHours(totals.utilization_minutes, isAr), className: "gold" },
        { label: t("workload_hours"), value: fmtHours(totals.workload_minutes, isAr), className: "gold" },
      ],
    });
  };

  return (
    <div className="space-y-5" data-testid="active-service-hours-report">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <p className="text-xs text-[var(--text-muted)] max-w-2xl">
          {t("active_service_hours_help")}
        </p>
        {dutyNote() && (
          <p className={`text-xs border rounded-md px-3 py-2 max-w-2xl ${dutyNoteTone()}`}>
            {dutyNote()}
          </p>
        )}
        <div className="flex gap-2">
          <button type="button" onClick={print} disabled={!data} className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Printer size={14}/>{isAr ? "طباعة" : "Print"}
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2" data-testid="ash-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {t("refresh")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: isAr ? "الموظفون" : "Staff", value: totals.staff_count ?? 0, icon: Users },
          { label: t("duty_days"), value: totals.duty_days ?? "—", icon: Clock },
          { label: t("shift_duty_avg"), value: fmtHours(totals.shift_duty_avg_minutes, isAr), icon: Clock },
          { label: t("utilization_hours"), value: fmtHours(totals.utilization_minutes, isAr), icon: Activity },
          { label: t("workload_hours"), value: fmtHours(totals.workload_minutes, isAr), icon: Layers },
          { label: t("idle_time_avg"), value: fmtHours(totals.idle_avg_minutes, isAr), icon: Coffee },
          { label: t("utilization_pct"), value: totals.utilization_pct != null ? `${totals.utilization_pct}%` : "—", icon: Activity },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="card-surface p-4">
            <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1">
              <Icon size={11} className="text-[var(--gold)]"/>{label}
            </div>
            <div className="text-xl font-bold mt-1 num-display gold-text">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-3 text-xs">
        <div className="card-surface p-4 border-s-4 border-s-violet-400">
          <div className="font-semibold text-[var(--text)] mb-1">{t("utilization_hours")}</div>
          <p className="text-[var(--text-muted)]">{t("utilization_hours_desc")}</p>
        </div>
        <div className="card-surface p-4 border-s-4 border-s-amber-400">
          <div className="font-semibold text-[var(--text)] mb-1">{t("workload_hours")}</div>
          <p className="text-[var(--text-muted)]">{t("workload_hours_desc")}</p>
        </div>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="table-dark min-w-full text-sm">
          <thead>
            <tr>
              <th>{isAr ? "الموظف" : "Employee"}</th>
              <th className="text-end">{isAr ? "الجلسات" : "Sessions"}</th>
              <th className="text-end">{t("duty_days")}</th>
              <th className="text-end">{t("shift_duty_avg")}</th>
              <th className="text-end">{t("utilization_hours")}</th>
              <th className="text-end">{t("workload_hours")}</th>
              <th className="text-end">{t("overlap_time")}</th>
              <th className="text-end">{t("idle_time_avg")}</th>
              <th className="text-end">{t("utilization_pct")}</th>
              <th className="text-end">{t("concurrency_factor")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const key = r.employee_user_id || r.employee_id || `row-${idx}`;
              return (
                <tr key={key} data-testid={`ash-row-${key}`}>
                  <td>
                    <div className="font-medium">{r.employee_name}</div>
                    {r.employee_code && <div className="text-[10px] text-[var(--text-muted)]">{r.employee_code}</div>}
                  </td>
                  <td className="text-end num-display">{r.sessions_count || 0}</td>
                  <td className="text-end num-display">{r.duty_days ?? "—"}</td>
                  <td className="text-end num-display">
                    <div>{fmtHours(r.shift_duty_avg_minutes, isAr)}</div>
                    {(r.shift_duty_minutes ?? 0) > 0 && (
                      <div className="text-[10px] text-[var(--text-muted)]">{isAr ? "المجموع" : "total"} {fmtHours(r.shift_duty_minutes, isAr)}</div>
                    )}
                  </td>
                  <td className="text-end num-display font-semibold text-violet-700">{fmtHours(r.utilization_minutes, isAr)}</td>
                  <td className="text-end num-display font-semibold text-amber-700">{fmtHours(r.workload_minutes, isAr)}</td>
                  <td className="text-end num-display text-[var(--text-muted)]">{fmtHours(r.overlap_minutes, isAr)}</td>
                  <td className="text-end num-display">
                    <div>{fmtHours(r.idle_avg_minutes, isAr)}</div>
                    {r.idle_minutes != null && (
                      <div className="text-[10px] text-[var(--text-muted)]">{isAr ? "المجموع" : "total"} {fmtHours(r.idle_minutes, isAr)}</div>
                    )}
                  </td>
                  <td className="text-end num-display">
                    {r.utilization_pct != null ? (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        r.utilization_pct >= 70 ? "bg-emerald-100 text-emerald-800"
                        : r.utilization_pct >= 40 ? "bg-amber-100 text-amber-800"
                        : "bg-stone-100 text-stone-600"
                      }`}>
                        {r.utilization_pct}%
                      </span>
                    ) : "—"}
                  </td>
                  <td className="text-end num-display">{r.concurrency_factor ?? "—"}</td>
                </tr>
              );
            })}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={10} className="text-center py-10 text-[var(--text-muted)]">{isAr ? "لا توجد بيانات" : "No data for this period"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
